import { Box, Button, Heading, Icon, Text } from '@medely/ui-kit';
import { ContentLoader, FixedFooterLayout, JobDetailsListItem } from '@mp/components';
import { debounce } from 'lodash';
import React, { useMemo, useState } from 'react';
import { usePhoneScreeningScheduledData } from './usePhoneScreeningScheduledData';
import { useApplicationMutations } from '../../applicationFlowMutations';

const RESCHEDULE_DEBOUNCE_DELAY = 1000;

export const PhoneScreeningScheduled = ({ isMobile = false }: { isMobile?: boolean }) => {
  const { isLoading, date, time, recruiterMessage, cancelScreening, isMutationPending } =
    usePhoneScreeningScheduledData();

  const { prevStep } = useApplicationMutations();
  const [isRescheduling, setIsRescheduling] = useState(false);

  const debouncedReschedule = useMemo(() => {
    const handleReschedule = () => {
      if (isRescheduling || isMutationPending) {
        return;
      }

      setIsRescheduling(true);
      cancelScreening(undefined, {
        onSuccess: () => {
          prevStep();
        },
      });

      setTimeout(() => {
        setIsRescheduling(false);
      }, RESCHEDULE_DEBOUNCE_DELAY);
    };

    return debounce(handleReschedule, RESCHEDULE_DEBOUNCE_DELAY, {
      leading: true,
      trailing: false,
    });
  }, [isRescheduling, isMutationPending, cancelScreening, prevStep]);

  const isButtonDisabled = isRescheduling || isMutationPending;

  return (
    <ContentLoader loading={isLoading}>
      <FixedFooterLayout
        footerContent={
          <Box py={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={debouncedReschedule}
              loading={isButtonDisabled}
              disabled={isButtonDisabled}
              fullWidth
            >
              Reschedule
            </Button>
          </Box>
        }
        height="97%"
      >
        <Box mb={2}>
          <Heading size="m" color="text.primary" textAlign={isMobile ? 'center' : 'left'}>
            Your interview is confirmed
          </Heading>
        </Box>
        <Box py={2}>
          <JobDetailsListItem
            icon={
              <Icon name="light-calendar" iconSize="20px" color="text.primary" variant="custom" />
            }
            title="Date"
          >
            <Text size="m">{date}</Text>
          </JobDetailsListItem>
        </Box>
        <Box py={2}>
          <JobDetailsListItem
            icon={<Icon name="light-clock" iconSize="20px" color="text.primary" variant="custom" />}
            title="Time"
          >
            <Text size="m">{time}</Text>
          </JobDetailsListItem>
        </Box>
        <Box py={2}>
          <JobDetailsListItem
            icon={<Icon name="user-search" iconSize="20px" color="text.primary" variant="custom" />}
            title="Your clinical specialist"
          >
            <Text size="m">{recruiterMessage}</Text>
          </JobDetailsListItem>
        </Box>
      </FixedFooterLayout>
    </ContentLoader>
  );
};
